import { Grid, Heading, Stack } from '@global-ecom/nitro-uds/elements';

import { useTranslate } from 'hooks/useTranslations';
import { useSticky } from 'hooks/useSticky';
import { ProductStory } from 'ui/components/ProductStory';
import {
  ProductBreadcrumb,
  ProductImageGallery,
  ProductVariantSelectors,
} from 'ui/pages/ProductDetailsPages';
import { SanityContentSlot } from 'ui/components/SanityContentSlot';
import { ManagedHeading } from 'ui/components/ManagedHeading';
import { EmplifiStarsBelowPDPTitle } from 'ui/content/Emplifi/StarsBelowPDPTitle';
import { useSiteConfig } from 'hooks/useSiteConfig';

import { ProductRecommendations } from './ProductRecommendations';
import { SoldOutBanner } from './SoldOutBanner';
import { PdpProps } from './ProductDetailsPage';

export const PdpSoldoutWithRecommenderLayout = ({
  masterProduct,
  variation,
  content,
}: PdpProps) => {
  const t = useTranslate();
  const { stickyRef, stickyTop } = useSticky(3);
  const {
    staticFeatures: { isPNARegion },
  } = useSiteConfig();
  const recommender = content?.soldOutRecommender;
  const isMasterSoldOutWithRecommender =
    masterProduct?.displayOutOfStock?.soldoutWithRecommender;

  const productName = variation?.name || masterProduct?.name;

  return (
    <>
      <ProductBreadcrumb />
      <Stack asChild gap={{ _: 'sm', sm: 'lg' }}>
        <section
          aria-labelledby="pdp-product-title"
          className="mobile:mt-4 tablet:mt-6 desktop:mt-8"
        >
          <Grid columns={{ _: '1', md: '8', lg: '12' }}>
            <ProductImageGallery className="grid-cols-1" />
            <section
              aria-label={t('productInfo')}
              className="order-1 h-full md:col-span-4 lg:col-span-4"
            >
              <Stack
                ref={stickyRef}
                gap="base"
                className={`md:sticky md:top-[${stickyTop}px]`}
              >
                <Heading
                  asChild
                  size={{ _: 'base', md: 'lg', xl: 'xl' }}
                  weight="bold"
                >
                  <ManagedHeading
                    id="pdp-product-title"
                    dataTestId="pdp-title"
                    dangerouslySetInnerHTML={{ __html: productName! }}
                    tag="h1"
                  />
                </Heading>
                {isPNARegion && (
                  <EmplifiStarsBelowPDPTitle sku={variation?.id || ''} />
                )}
                {!isMasterSoldOutWithRecommender && <ProductVariantSelectors />}
                {isMasterSoldOutWithRecommender && (
                  <>
                    <SoldOutBanner />
                    <ProductRecommendations
                      masterProduct={masterProduct}
                      recommender={recommender}
                      content={content}
                    />
                  </>
                )}
              </Stack>
            </section>
          </Grid>
          {variation?.productStory && (
            <ProductStory
              {...variation.productStory}
              additionalContentSections={
                content?.ProductStoryAdditionalContentSlot
              }
              documentId={content?._id}
              slotId="ProductStoryAdditionalContentSlot"
            />
          )}
          <SanityContentSlot
            items={content?.belowProductStory}
            slotId="belowProductStory"
            documentId={content?._id}
          />
        </section>
      </Stack>
    </>
  );
};
